import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import {
  AdminTable, AdminModal, ConfirmDialog, FormField,
  LoadingState, ErrorState, ApiModeBanner, inputCls,
} from "../components/AdminUI";
import { useAdminT } from "../../../hooks/useAdminT";
import { useToast } from "../../../components/base/Toast";
import { useAdminData } from "../../../store/adminDataStore";
import type { Order, OrderStatus, PaymentStatus } from "../../../store/types";

const NOW = () => new Date().toISOString();

const ORDER_STATUSES: OrderStatus[] = ["pending", "confirmed", "cancelled", "completed"];
const PAYMENT_STATUSES: PaymentStatus[] = ["unpaid", "paid", "refunded", "partial"];

const ORDER_STATUS_STYLES: Record<OrderStatus, string> = {
  pending: "bg-amber-50 text-amber-600",
  confirmed: "bg-emerald-50 text-emerald-600",
  cancelled: "bg-[#D9D9D9]/50 text-[#2C2C2C]/40",
  completed: "bg-[#C8B8A6]/20 text-[#C8B8A6]",
};

const PAYMENT_STATUS_STYLES: Record<PaymentStatus, string> = {
  unpaid: "bg-red-50 text-red-400",
  paid: "bg-emerald-50 text-emerald-600",
  refunded: "bg-[#D9D9D9]/50 text-[#2C2C2C]/40",
  partial: "bg-amber-50 text-amber-600",
};

function emptyOrder(): Order {
  return {
    id: `ord-${Date.now()}`,
    order_number: `ORD-${Date.now()}`,
    customer_name: "", customer_email: "", customer_phone: "",
    service_id: "", selected_city: "",
    shoot_date: new Date().toISOString().slice(0, 10),
    start_time: "09:00", end_time: "18:00",
    number_of_reels: 1,
    subtotal_twd: 0, addons_total_twd: 0, total_twd: 0,
    currency: "TWD",
    order_status: "pending", payment_status: "unpaid",
    special_requests: "", internal_notes: "",
    created_at: NOW(), updated_at: NOW(),
  };
}

function LinkedResourcesPanel({
  orderId, costCount, noteCount, cancelCount, rescheduleCount, onNavigate,
}: {
  orderId: string; costCount: number; noteCount: number;
  cancelCount: number; rescheduleCount: number;
  onNavigate: (path: string) => void;
}) {
  const t = useAdminT();
  const items = [
    { label: t("tabifotoAdmin.orders.related_cost_items"), count: costCount, path: `/admin/booking-cost-items?order=${orderId}`, icon: "ri-money-dollar-circle-line" },
    { label: t("tabifotoAdmin.orders.related_notes"), count: noteCount, path: `/admin/booking-notes?order=${orderId}`, icon: "ri-sticky-note-line" },
    { label: t("tabifotoAdmin.orders.related_cancellations"), count: cancelCount, path: `/admin/order-cancellation-logs?order=${orderId}`, icon: "ri-close-circle-line" },
    { label: t("tabifotoAdmin.orders.related_reschedules"), count: rescheduleCount, path: `/admin/order-reschedule-logs?order=${orderId}`, icon: "ri-calendar-2-line" },
  ];
  return (
    <div className="mt-5 pt-5 border-t border-[#D9D9D9]/60">
      <p className="text-xs text-[#2C2C2C]/40 uppercase tracking-wider font-light mb-3">
        {t("tabifotoAdmin.orders.related_records")}
      </p>
      <div className="grid grid-cols-4 gap-2">
        {items.map((item) => (
          <button key={item.path} onClick={() => onNavigate(item.path)}
            className="flex flex-col items-center gap-1.5 p-3 bg-[#F7F6F3] rounded-lg hover:bg-[#C8B8A6]/10 transition-colors cursor-pointer">
            <div className="w-5 h-5 flex items-center justify-center text-[#2C2C2C]/40">
              <i className={`${item.icon} text-sm`} />
            </div>
            <span className={`text-lg font-light ${item.count > 0 ? "text-[#2C2C2C]" : "text-[#2C2C2C]/20"}`}>{item.count}</span>
            <span className="text-xs text-[#2C2C2C]/40 font-light text-center leading-tight">{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function EditModal({
  form, onChange, onClose, onSave, saving, isNew, serviceOptions, onNavigate,
  costCount, noteCount, cancelCount, rescheduleCount,
}: {
  form: Order; onChange: (o: Order) => void; onClose: () => void;
  onSave: () => void; saving: boolean; isNew: boolean;
  serviceOptions: Array<{ id: string; name: string }>;
  onNavigate: (path: string) => void;
  costCount: number; noteCount: number; cancelCount: number; rescheduleCount: number;
}) {
  const t = useAdminT();
  const set = (field: keyof Order, value: unknown) => onChange({ ...form, [field]: value });
  return (
    <AdminModal
      title={isNew ? t("tabifotoAdmin.orders.add_order") : t("tabifotoAdmin.orders.edit_order")}
      onClose={onClose} onSave={onSave} saving={saving}
    >
      <div className="grid grid-cols-2 gap-4">
        <FormField label={t("tabifotoAdmin.orders.customer_name")} required>
          <input className={inputCls} value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} />
        </FormField>
        <FormField label={t("tabifotoAdmin.orders.customer_email")}>
          <input className={inputCls} type="email" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} />
        </FormField>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormField label={t("tabifotoAdmin.orders.customer_phone")}>
          <input className={inputCls} value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} />
        </FormField>
        <FormField label={t("tabifotoAdmin.orders.selected_city")} required>
          <input className={inputCls} value={form.selected_city} onChange={(e) => set("selected_city", e.target.value)} placeholder="Kyoto" />
        </FormField>
      </div>
      <FormField label={t("tabifotoAdmin.orders.service_id")}>
        {serviceOptions.length > 0 ? (
          <select className={inputCls} value={form.service_id} onChange={(e) => set("service_id", e.target.value)}>
            <option value="">—</option>
            {serviceOptions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        ) : (
          <input className={inputCls} value={form.service_id} onChange={(e) => set("service_id", e.target.value)} />
        )}
      </FormField>
      <div className="grid grid-cols-3 gap-4">
        <FormField label={t("tabifotoAdmin.orders.shoot_date")} required>
          <input className={inputCls} type="date" value={form.shoot_date} onChange={(e) => set("shoot_date", e.target.value)} />
        </FormField>
        <FormField label={t("tabifotoAdmin.orders.start_time")}>
          <input className={inputCls} type="time" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} />
        </FormField>
        <FormField label={t("tabifotoAdmin.orders.end_time")}>
          <input className={inputCls} type="time" value={form.end_time} onChange={(e) => set("end_time", e.target.value)} />
        </FormField>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <FormField label={t("tabifotoAdmin.orders.subtotal_twd")}>
          <input className={inputCls} type="number" value={form.subtotal_twd} onChange={(e) => set("subtotal_twd", Number(e.target.value))} />
        </FormField>
        <FormField label={t("tabifotoAdmin.orders.addons_total_twd")}>
          <input className={inputCls} type="number" value={form.addons_total_twd} onChange={(e) => set("addons_total_twd", Number(e.target.value))} />
        </FormField>
        <FormField label={t("tabifotoAdmin.orders.total_twd")}>
          <input className={inputCls} type="number" value={form.total_twd} onChange={(e) => set("total_twd", Number(e.target.value))} />
        </FormField>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormField label={t("tabifotoAdmin.orders.order_status")} required>
          <select className={inputCls} value={form.order_status} onChange={(e) => set("order_status", e.target.value as OrderStatus)}>
            {ORDER_STATUSES.map((s) => <option key={s} value={s}>{t(`tabifotoAdmin.orders.order_status_${s}`)}</option>)}
          </select>
        </FormField>
        <FormField label={t("tabifotoAdmin.orders.payment_status")} required>
          <select className={inputCls} value={form.payment_status} onChange={(e) => set("payment_status", e.target.value as PaymentStatus)}>
            {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{t(`tabifotoAdmin.orders.payment_status_${s}`)}</option>)}
          </select>
        </FormField>
      </div>
      <FormField label={t("tabifotoAdmin.orders.special_requests")}>
        <textarea className={`${inputCls} h-16 resize-none`} value={form.special_requests} onChange={(e) => set("special_requests", e.target.value)} maxLength={500} />
      </FormField>
      <FormField label={t("tabifotoAdmin.orders.internal_notes")}>
        <textarea className={`${inputCls} h-16 resize-none`} value={form.internal_notes} onChange={(e) => set("internal_notes", e.target.value)} maxLength={500} />
      </FormField>
      {!isNew && (
        <LinkedResourcesPanel orderId={form.id}
          costCount={costCount} noteCount={noteCount}
          cancelCount={cancelCount} rescheduleCount={rescheduleCount}
          onNavigate={onNavigate} />
      )}
    </AdminModal>
  );
}

export default function AdminOrdersPage() {
  const t = useAdminT();
  const { state, status, isApiMode, upsertOrder, removeOrder, refetchOrders } = useAdminData();
  const toast = useToast();
  const navigate = useNavigate();

  const [editItem, setEditItem] = useState<Order | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [filterOrder, setFilterOrder] = useState<OrderStatus | "all">("all");
  const [filterPayment, setFilterPayment] = useState<PaymentStatus | "all">("all");

  const serviceOptions = state.services.filter((s) => s.is_active).map((s) => ({
    id: s.service_code || s.id,
    name: s.title_zh || s.title_en,
  }));

  const filtered = [...state.orders]
    .filter((o) => {
      if (filterOrder !== "all" && o.order_status !== filterOrder) return false;
      if (filterPayment !== "all" && o.payment_status !== filterPayment) return false;
      return true;
    })
    .sort((a, b) => a.created_at < b.created_at ? 1 : -1);

  const handleSave = async () => {
    if (!editItem) return;
    setSaving(true);
    try {
      await upsertOrder(editItem);
      toast.success(t("tabifotoAdmin.common.save_success"));
      setEditItem(null);
    } catch { toast.error(t("tabifotoAdmin.common.save_error")); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await removeOrder(deleteId);
      toast.success(t("tabifotoAdmin.common.delete_success"));
    } catch { toast.error(t("tabifotoAdmin.common.delete_error")); }
    finally { setDeleteId(null); }
  };

  const handleNavigateAndClose = (path: string) => {
    setEditItem(null);
    navigate(path);
  };

  const getRelatedCounts = (orderId: string) => ({
    costCount: state.bookingCostItems.filter((c) => c.order_id === orderId).length,
    noteCount: state.bookingNotes.filter((n) => n.order_id === orderId).length,
    cancelCount: state.orderCancellationLogs.filter((l) => l.order_id === orderId).length,
    rescheduleCount: state.orderRescheduleLogs.filter((l) => l.order_id === orderId).length,
  });

  const ORDER_FILTER_TABS = [
    { key: "all" as const, label: t("tabifotoAdmin.orders.filter_all") },
    ...ORDER_STATUSES.map((s) => ({ key: s, label: t(`tabifotoAdmin.orders.order_status_${s}`) })),
  ];

  const PAYMENT_FILTER_TABS = [
    { key: "all" as const, label: t("tabifotoAdmin.orders.filter_all") },
    ...PAYMENT_STATUSES.map((s) => ({ key: s, label: t(`tabifotoAdmin.orders.payment_status_${s}`) })),
  ];

  const headers = [
    t("tabifotoAdmin.orders.col_order_number"),
    t("tabifotoAdmin.orders.col_customer"),
    t("tabifotoAdmin.orders.col_city"),
    t("tabifotoAdmin.orders.col_shoot_date"),
    t("tabifotoAdmin.orders.col_total"),
    t("tabifotoAdmin.orders.col_order_status"),
    t("tabifotoAdmin.orders.col_payment_status"),
    t("tabifotoAdmin.orders.col_actions"),
  ];

  const editCounts = editItem ? getRelatedCounts(editItem.id) : { costCount: 0, noteCount: 0, cancelCount: 0, rescheduleCount: 0 };

  return (
    <AdminLayout
      title={t("tabifotoAdmin.orders.title")}
      subtitle={t("tabifotoAdmin.orders.subtitle")}
      action={
        <button onClick={() => setEditItem(emptyOrder())}
          className="flex items-center gap-2 px-4 py-2 bg-[#2C2C2C] text-white text-sm rounded-md hover:bg-[#2C2C2C]/80 transition-colors cursor-pointer whitespace-nowrap">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-base" /></div>
          {t("tabifotoAdmin.orders.add_order")}
        </button>
      }
    >
      <ApiModeBanner isApiMode={isApiMode} />

      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="flex gap-1 bg-[#F7F6F3] p-1 rounded-lg">
          {ORDER_FILTER_TABS.map((tab) => (
            <button key={tab.key} onClick={() => setFilterOrder(tab.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-light transition-colors cursor-pointer whitespace-nowrap ${filterOrder === tab.key ? "bg-white text-[#2C2C2C]" : "text-[#2C2C2C]/40 hover:text-[#2C2C2C]/70"}`}>
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1 bg-[#F7F6F3] p-1 rounded-lg">
          {PAYMENT_FILTER_TABS.map((tab) => (
            <button key={tab.key} onClick={() => setFilterPayment(tab.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-light transition-colors cursor-pointer whitespace-nowrap ${filterPayment === tab.key ? "bg-white text-[#2C2C2C]" : "text-[#2C2C2C]/40 hover:text-[#2C2C2C]/70"}`}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {status.orders.loading ? <LoadingState /> :
        status.orders.error ? <ErrorState message={status.orders.error} onRetry={refetchOrders} /> : (
          <AdminTable headers={headers} empty={filtered.length === 0} emptyMessage={t("tabifotoAdmin.orders.no_orders")}>
            {filtered.map((o) => {
              const counts = getRelatedCounts(o.id);
              const hasLinked = counts.costCount + counts.noteCount + counts.cancelCount + counts.rescheduleCount > 0;
              return (
                <tr key={o.id} className="border-b border-[#D9D9D9]/40 hover:bg-[#F7F6F3]/60 transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-mono text-[#2C2C2C]/50">{o.order_number}</span>
                      {hasLinked && (
                        <div className="w-1.5 h-1.5 rounded-full bg-[#C8B8A6]" title="Has related records" />
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <div>
                      <p className="text-sm font-light text-[#2C2C2C]">{o.customer_name}</p>
                      <p className="text-xs text-[#2C2C2C]/40 mt-0.5">{o.customer_email}</p>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-xs text-[#2C2C2C]/60">{o.selected_city}</td>
                  <td className="px-5 py-3 text-xs text-[#2C2C2C]/60">{o.shoot_date}</td>
                  <td className="px-5 py-3">
                    <span className="text-sm font-light text-[#2C2C2C]">NT${o.total_twd.toLocaleString()}</span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-light ${ORDER_STATUS_STYLES[o.order_status]}`}>
                      {t(`tabifotoAdmin.orders.order_status_${o.order_status}`)}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-light ${PAYMENT_STATUS_STYLES[o.payment_status]}`}>
                      {t(`tabifotoAdmin.orders.payment_status_${o.payment_status}`)}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setEditItem({ ...o })}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-[#2C2C2C]/40 hover:text-[#2C2C2C] hover:bg-[#F7F6F3] transition-colors cursor-pointer">
                        <i className="ri-edit-line text-base" />
                      </button>
                      <button onClick={() => setDeleteId(o.id)}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-[#2C2C2C]/40 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer">
                        <i className="ri-delete-bin-line text-base" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </AdminTable>
        )}

      {editItem && (
        <EditModal form={editItem} onChange={setEditItem} onClose={() => setEditItem(null)}
          onSave={handleSave} saving={saving}
          isNew={!state.orders.some((o) => o.id === editItem.id)}
          serviceOptions={serviceOptions}
          onNavigate={handleNavigateAndClose}
          costCount={editCounts.costCount}
          noteCount={editCounts.noteCount}
          cancelCount={editCounts.cancelCount}
          rescheduleCount={editCounts.rescheduleCount}
        />
      )}

      {deleteId && (
        <ConfirmDialog title={t("tabifotoAdmin.orders.delete_confirm")}
          message={t("tabifotoAdmin.common.cannot_undone")} danger
          onConfirm={handleDelete} onCancel={() => setDeleteId(null)} />
      )}
    </AdminLayout>
  );
}
